/** Shared buylist offer / market display helpers (submission line UI). */

import type { PolicyPriceDebug } from '../components/pricing/PolicySignalsPanel';

/** Offer above this % of a reference market → staff review before intake. */
export const BUYLIST_HIGH_OFFER_PCT = 90;

export type BuylistMarketKey = 'tcgLow' | 'tcgMarket' | 'manapoolMarket' | 'ckBuylist';

export const BUYLIST_MARKET_LABELS: Record<BuylistMarketKey, string> = {
  tcgLow: 'TCG Low',
  tcgMarket: 'TCG Market',
  manapoolMarket: 'Mana Pool',
  ckBuylist: 'CK Buylist',
};

/**
 * Market that drives the >90% high-offer review flag.
 * TCG / CK stay visible for diagnosis (high MP % is often explained by a high CK floor),
 * but they are not the flag denominator.
 */
export const BUYLIST_HIGH_OFFER_MARKET_KEYS: readonly BuylistMarketKey[] = [
  'manapoolMarket',
] as const;

/** Sell/buylist markets that count as "has pricing" for channel sync readiness. */
export const BUYLIST_SYNC_MARKET_KEYS: readonly BuylistMarketKey[] = [
  'tcgMarket',
  'manapoolMarket',
  'ckBuylist',
] as const;

export type BuylistMarketSignals = {
  tcgLow?: number | null;
  tcgMarket?: number | null;
  manapoolMarket?: number | null;
  ckBuylist?: number | null;
};

export type BuylistReviewFlag =
  | { kind: 'missing_pricing'; label: string }
  | { kind: 'high_offer'; label: string; marketKey: BuylistMarketKey; percent: number };

export type BuylistLotMarkets = {
  tcgLow: number | null;
  tcgMarket: number | null;
  manapoolMarket: number | null;
  ckBuylist: number | null;
  offerTotal: number | null;
  /** Sum of live policy unit × qty. Null when no line contributed a policy price. */
  policyPrice: number | null;
};

export function formatMoneyAmount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '—';
  }
  return `$${Number(value).toFixed(2)}`;
}

/** offer ÷ market as a percentage; null when either side is missing/zero/non-finite. */
export function offerPercentOfMarket(
  offer: number | null | undefined,
  market: number | null | undefined,
): number | null {
  if (offer === null || offer === undefined || market === null || market === undefined) {
    return null;
  }
  const o = Number(offer);
  const m = Number(market);
  if (!Number.isFinite(o) || !Number.isFinite(m) || m === 0) {
    return null;
  }
  return (o / m) * 100;
}

export function formatOfferPercent(
  offer: number | null | undefined,
  market: number | null | undefined,
): string {
  const pct = offerPercentOfMarket(offer, market);
  if (pct === null) return '—';
  return `${Math.round(pct)}%`;
}

export function isFiniteMoney(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && Number.isFinite(Number(value));
}

/** True when at least one sync-relevant market (TCG Market / Mana Pool / CK) is present. */
export function hasSyncPricingData(markets: BuylistMarketSignals): boolean {
  return BUYLIST_SYNC_MARKET_KEYS.some((key) => isFiniteMoney(markets[key]));
}

/**
 * Review flags for a submission line (pre-intake).
 * - missing_pricing: matched line with no TCG Market / Mana Pool / CK Buylist
 *   (channel preferred-price stamp / sync historically fails without markets)
 * - high_offer: unit offer > 90% of Mana Pool Market only
 */
export function buylistLineReviewFlags(
  offerUnit: number | null | undefined,
  markets: BuylistMarketSignals,
  opts?: { hasCatalogId?: boolean; skip?: boolean },
): BuylistReviewFlag[] {
  if (opts?.skip) return [];
  const flags: BuylistReviewFlag[] = [];
  const hasCatalog = opts?.hasCatalogId !== false;

  if (hasCatalog && !hasSyncPricingData(markets)) {
    flags.push({
      kind: 'missing_pricing',
      label: 'No pricing data — review before intake (channel sync needs markets)',
    });
  }

  for (const key of BUYLIST_HIGH_OFFER_MARKET_KEYS) {
    const pct = offerPercentOfMarket(offerUnit, markets[key]);
    if (pct !== null && pct > BUYLIST_HIGH_OFFER_PCT) {
      flags.push({
        kind: 'high_offer',
        marketKey: key,
        percent: pct,
        label: `Offer ${Math.round(pct)}% of ${BUYLIST_MARKET_LABELS[key]} (>${BUYLIST_HIGH_OFFER_PCT}%)`,
      });
    }
  }

  return flags;
}

export type BuylistQtyLine = {
  quantity?: number | null;
  expectedQuantity?: number | null;
  receivedQuantity?: number | null;
  lineCheckedAt?: string | null;
};

/** Declared snapshot. Legacy payloads without the field use quantity. */
export function lineExpectedQuantity(item: BuylistQtyLine): number {
  if (typeof item.expectedQuantity === 'number' && Number.isFinite(item.expectedQuantity)) {
    return Math.max(0, item.expectedQuantity);
  }
  return Math.max(0, Number(item.quantity) || 0);
}

/**
 * Copies in hand. New payloads send receivedQuantity (0 until checked in).
 * Legacy payloads: a checklist stamp means the whole quantity; otherwise quantity
 * was the only count and is treated as already payable.
 */
export function lineReceivedQuantity(item: BuylistQtyLine): number {
  if (typeof item.receivedQuantity === 'number' && Number.isFinite(item.receivedQuantity)) {
    return Math.max(0, item.receivedQuantity);
  }
  if (typeof item.expectedQuantity === 'number') {
    return item.lineCheckedAt ? lineExpectedQuantity(item) : 0;
  }
  if (item.lineCheckedAt) return Math.max(0, Number(item.quantity) || 0);
  return Math.max(0, Number(item.quantity) || 0);
}

/** True when every expected copy is in hand (or the legacy line is stamped). */
export function lineFullyReceived(item: BuylistQtyLine): boolean {
  const expected = lineExpectedQuantity(item);
  if (expected <= 0) return Boolean(item.lineCheckedAt);
  return lineReceivedQuantity(item) >= expected;
}

/** Checklist progress is copies received vs expected, not a boolean per line. */
export function buylistCopyProgress(lines: readonly BuylistQtyLine[]): {
  received: number;
  expected: number;
} {
  let received = 0;
  let expected = 0;
  for (const line of lines) {
    received += lineReceivedQuantity(line);
    expected += lineExpectedQuantity(line);
  }
  return { received, expected };
}

function addMoney(acc: number | null, unit: number | null | undefined, qty: number): number | null {
  if (!isFiniteMoney(unit) || !Number.isFinite(qty) || qty <= 0) return acc;
  const line = Number(unit) * qty;
  return acc === null ? line : acc + line;
}

/**
 * Lot-level market totals: sum(unit market × qty) and sum(line offer) over active lines.
 * Null totals stay null when no line contributed that market (no invented zeros).
 */
export function aggregateBuylistLotMarkets(
  lines: Array<
    BuylistMarketSignals & {
      quantity?: number | null;
      offerTotal?: number | null;
      policyPrice?: number | null;
    }
  >,
): BuylistLotMarkets {
  let tcgLow: number | null = null;
  let tcgMarket: number | null = null;
  let manapoolMarket: number | null = null;
  let ckBuylist: number | null = null;
  let offerTotal: number | null = null;
  let policyPrice: number | null = null;

  for (const line of lines) {
    const qty = Number(line.quantity ?? 0);
    tcgLow = addMoney(tcgLow, line.tcgLow, qty);
    tcgMarket = addMoney(tcgMarket, line.tcgMarket, qty);
    manapoolMarket = addMoney(manapoolMarket, line.manapoolMarket, qty);
    ckBuylist = addMoney(ckBuylist, line.ckBuylist, qty);
    policyPrice = addMoney(policyPrice, line.policyPrice, qty);
    if (isFiniteMoney(line.offerTotal)) {
      offerTotal = offerTotal === null ? Number(line.offerTotal) : offerTotal + Number(line.offerTotal);
    }
  }

  return { tcgLow, tcgMarket, manapoolMarket, ckBuylist, offerTotal, policyPrice };
}

const BUYLIST_LOT_EXCLUDED_STATUSES = new Set(['rejected_on_grading', 'unmatched', 'superseded']);

/**
 * Lines that count toward receive-checklist lot totals (markets, offer, policy).
 * Rejected, unmatched, and superseded stay out — same set the checklist table uses.
 */
export function isActiveBuylistReceiveLine(item: {
  status?: string | null;
  included?: boolean | null;
}): boolean {
  const status = item.status || '';
  if (status === 'matched' || status === 'accepted_on_grading') return true;
  if (!item.included) return false;
  return !BUYLIST_LOT_EXCLUDED_STATUSES.has(status);
}

/** Lot-level high-offer flags using the same Mana Pool Market threshold. */
export function buylistLotReviewFlags(lot: BuylistLotMarkets): BuylistReviewFlag[] {
  return buylistLineReviewFlags(lot.offerTotal, lot, { hasCatalogId: true });
}

/**
 * Statuses `compute_final_offer` skips before the included flag.
 * Keep this set aligned with inventory `compute_final_offer`.
 */
const BUYLIST_FINAL_OFFER_EXCLUDED_STATUSES = new Set([
  'superseded',
  'rejected_on_grading',
  'unmatched',
]);

/** True when a line contributes to the customer payout (`compute_final_offer`). */
export function buylistLineIncludedInFinalOffer(line: {
  status?: string | null;
  included?: boolean | null;
}): boolean {
  const status = (line.status || '').trim();
  if (BUYLIST_FINAL_OFFER_EXCLUDED_STATUSES.has(status)) return false;
  if (!line.included) return false;
  return true;
}

/**
 * Offer dollars for one line: graded line total wins over quoted.
 * A graded total of $0 is a real offer. Null only when neither total is finite.
 */
export function buylistLineOfferTotal(line: {
  gradedLineTotal?: number | null;
  quotedLineTotal?: number | null;
}): number | null {
  if (isFiniteMoney(line.gradedLineTotal)) return Number(line.gradedLineTotal);
  if (isFiniteMoney(line.quotedLineTotal)) return Number(line.quotedLineTotal);
  return null;
}

export type BuylistReceiveTotalLine = {
  status?: string | null;
  included?: boolean | null;
  gradedLineTotal?: number | null;
  quotedLineTotal?: number | null;
  gradedUnitOffer?: number | null;
  quotedUnitOffer?: number | null;
  quantity?: number | null;
  /** Set when staff have verified the physical line (full check-in). */
  lineCheckedAt?: string | null;
  /**
   * Partial check-in. When a finite number (including 0), offer dollars follow
   * received units. Omit until the partial-qty payload exists — a check then
   * counts the current line total (full quantity).
   */
  receivedQuantity?: number | null;
  /**
   * Expected copies. When omitted, `quantity` is the expected count
   * (today's payable qty). Pass this once quantity becomes received-only.
   */
  expectedQuantity?: number | null;
};

export type BuylistReceiveTotals = {
  /** Offer dollars checked in (customer payout, not policy or market). */
  received: number;
  /** Offer dollars on included lines not yet checked in. */
  stillOpen: number;
  /**
   * Current offer on lines excluded from the final offer.
   * Not a historical delta of dollars removed.
   */
  excluded: number;
};

function finiteNonNegative(value: number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function addCents(cents: number, dollars: number): number {
  if (!Number.isFinite(dollars)) return cents;
  return cents + Math.round(dollars * 100);
}

/**
 * Unit offer used to prorate a partial receive.
 * Graded unit wins; otherwise a graded line total (including $0) beats quoted.
 */
function buylistLineOfferUnit(line: BuylistReceiveTotalLine): number | null {
  if (isFiniteMoney(line.gradedUnitOffer)) return Number(line.gradedUnitOffer);
  const qty = finiteNonNegative(line.quantity);
  if (isFiniteMoney(line.gradedLineTotal)) {
    if (qty != null && qty > 0) return Number(line.gradedLineTotal) / qty;
    return Number(line.gradedLineTotal) === 0 ? 0 : null;
  }
  if (isFiniteMoney(line.quotedUnitOffer)) return Number(line.quotedUnitOffer);
  if (isFiniteMoney(line.quotedLineTotal) && qty != null && qty > 0) {
    return Number(line.quotedLineTotal) / qty;
  }
  return null;
}

/**
 * Running receive totals in offer dollars (what we pay).
 *
 * Inclusion matches `compute_final_offer`: skip superseded, rejected_on_grading,
 * unmatched, and included=false. Graded line total wins over quoted.
 * Unmatched lines never count as received, even if the checklist is ticked.
 *
 * Until `receivedQuantity` is on the line, a tick counts the current line total.
 * When that field is a number, received units × unit offer count as received and
 * the remainder stays open (1 of 3 adds one unit).
 */
export function aggregateBuylistReceiveTotals(
  lines: readonly BuylistReceiveTotalLine[],
): BuylistReceiveTotals {
  let receivedCents = 0;
  let openCents = 0;
  let excludedCents = 0;

  for (const line of lines) {
    const offerTotal = buylistLineOfferTotal(line);
    if (!buylistLineIncludedInFinalOffer(line)) {
      if (offerTotal != null) excludedCents = addCents(excludedCents, offerTotal);
      continue;
    }

    const receivedQty = finiteNonNegative(line.receivedQuantity);
    if (receivedQty != null) {
      const expected =
        finiteNonNegative(line.expectedQuantity) ??
        finiteNonNegative(line.quantity) ??
        receivedQty;
      const cappedReceived = Math.min(receivedQty, expected);
      const unit = buylistLineOfferUnit(line);
      if (unit != null) {
        receivedCents = addCents(receivedCents, unit * cappedReceived);
        openCents = addCents(openCents, unit * Math.max(0, expected - cappedReceived));
      }
      continue;
    }

    if (offerTotal == null) continue;
    if (line.lineCheckedAt) receivedCents = addCents(receivedCents, offerTotal);
    else openCents = addCents(openCents, offerTotal);
  }

  return {
    received: receivedCents / 100,
    stillOpen: openCents / 100,
    excluded: excludedCents / 100,
  };
}

export const BUYLIST_CONTACT_SOURCES = [
  { id: 'website', label: 'Website' },
  { id: 'manapool', label: 'Mana Pool' },
  { id: 'local', label: 'Local' },
  { id: 'facebook', label: 'Facebook' },
] as const;

export type BuylistContactSource = (typeof BUYLIST_CONTACT_SOURCES)[number]['id'];

export function formatContactSource(value: string | null | undefined): string {
  if (!value) return '—';
  const hit = BUYLIST_CONTACT_SOURCES.find((s) => s.id === value);
  return hit?.label ?? value;
}

/** Staff checklist: isolate review-flagged lines while resolving pricing / offers. */
export type BuylistReviewFlagFilter =
  | 'all'
  | 'flagged'
  | 'missing_pricing'
  | 'high_offer'
  | 'unflagged';

export const BUYLIST_REVIEW_FLAG_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'flagged', label: 'Flagged (any)' },
  { id: 'missing_pricing', label: 'Missing pricing' },
  { id: 'high_offer', label: 'High offer' },
  { id: 'unflagged', label: 'Unflagged' },
] as const satisfies ReadonlyArray<{ id: BuylistReviewFlagFilter; label: string }>;

/**
 * Default sort for receive checklist: flagged first (missing pricing before high offer),
 * then hot Mana Pool %, then name — matches staff "work the flags down" flow.
 */
export type BuylistReviewLineSort = 'flag_severity' | 'offer_pct_manapool' | 'name';

export const BUYLIST_REVIEW_LINE_SORTS = [
  { id: 'flag_severity', label: 'Flag severity' },
  { id: 'offer_pct_manapool', label: 'Offer % of Mana Pool' },
  { id: 'name', label: 'Card name' },
] as const satisfies ReadonlyArray<{ id: BuylistReviewLineSort; label: string }>;

/** Higher = more urgent. missing_pricing (2) > high_offer (1) > none (0). */
export function reviewFlagSeverity(flags: readonly BuylistReviewFlag[]): number {
  let score = 0;
  for (const flag of flags) {
    if (flag.kind === 'missing_pricing') score = Math.max(score, 2);
    else if (flag.kind === 'high_offer') score = Math.max(score, 1);
  }
  return score;
}

export function lineMatchesReviewFlagFilter(
  flags: readonly BuylistReviewFlag[],
  filter: BuylistReviewFlagFilter,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'flagged':
      return flags.length > 0;
    case 'unflagged':
      return flags.length === 0;
    case 'missing_pricing':
      return flags.some((f) => f.kind === 'missing_pricing');
    case 'high_offer':
      return flags.some((f) => f.kind === 'high_offer');
    default:
      return true;
  }
}

export type BuylistReviewSortableLine = {
  cardName?: string | null;
  offerUnit?: number | null;
  manapoolMarket?: number | null;
  flags: readonly BuylistReviewFlag[];
};

function compareNullableNumberDesc(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1; // nulls last when sorting hot → cold
  if (b === null) return -1;
  return b - a;
}

function compareNameAsc(a: string | null | undefined, b: string | null | undefined): number {
  return (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });
}

/** Comparator for receive-checklist sort. Negative ⇒ a before b. */
export function compareBuylistReviewLines(
  a: BuylistReviewSortableLine,
  b: BuylistReviewSortableLine,
  sort: BuylistReviewLineSort,
): number {
  const aPct = offerPercentOfMarket(a.offerUnit, a.manapoolMarket);
  const bPct = offerPercentOfMarket(b.offerUnit, b.manapoolMarket);

  if (sort === 'name') {
    return compareNameAsc(a.cardName, b.cardName);
  }

  if (sort === 'offer_pct_manapool') {
    const byPct = compareNullableNumberDesc(aPct, bPct);
    if (byPct !== 0) return byPct;
    const bySeverity = reviewFlagSeverity(b.flags) - reviewFlagSeverity(a.flags);
    if (bySeverity !== 0) return bySeverity;
    return compareNameAsc(a.cardName, b.cardName);
  }

  // flag_severity (default): missing → high offer → clean, then hot %, then name
  const bySeverity = reviewFlagSeverity(b.flags) - reviewFlagSeverity(a.flags);
  if (bySeverity !== 0) return bySeverity;
  const byPct = compareNullableNumberDesc(aPct, bPct);
  if (byPct !== 0) return byPct;
  return compareNameAsc(a.cardName, b.cardName);
}

/** True when any live market signal is present on the line. */
export function lineHasLiveMarkets(line: BuylistMarketSignals): boolean {
  return (
    line.tcgLow != null ||
    line.tcgMarket != null ||
    line.manapoolMarket != null ||
    line.ckBuylist != null
  );
}

export type BuylistLiveMarketLine = BuylistMarketSignals & {
  id: string;
  mtgjsonUuid?: string | null;
  condition?: string | null;
  gradedCondition?: string | null;
  finish?: string | null;
  policyPrice?: number | null;
  policyPriceDebug?: PolicyPriceDebug | null;
  availableFinishes?: string[] | null;
  finishResolveSource?: string | null;
  finishBlocked?: boolean;
  finishNeedsManualPick?: boolean;
  finishBlockMessage?: string | null;
};

/** True when enrich already attached a policy quote (finite price, or debug even if the price is null). */
function lineHasLivePolicyStamp(line: {
  policyPrice?: number | null;
  policyPriceDebug?: PolicyPriceDebug | null;
}): boolean {
  return isFiniteMoney(line.policyPrice) || line.policyPriceDebug != null;
}

function lineHasFinishGuardrails(line: {
  availableFinishes?: string[] | null;
  finishResolveSource?: string | null;
}): boolean {
  return (
    (Array.isArray(line.availableFinishes) && line.availableFinishes.length > 0) ||
    Boolean(line.finishResolveSource)
  );
}

/**
 * After a scoped/skip-markets PATCH, reuse prior live stamps for unchanged lines.
 * Only copies markets and policy when uuid + condition + finish still match.
 * A fresh quote (markets or policy debug present) wins, including a null policy.
 */
export function mergeBuylistLiveMarketLines<T extends BuylistLiveMarketLine>(
  previous: readonly T[] | null | undefined,
  next: readonly T[],
): T[] {
  if (!previous?.length) return [...next];
  const prevById = new Map(previous.map((item) => [item.id, item]));
  return next.map((item) => {
    const hasFresh =
      lineHasLiveMarkets(item) ||
      lineHasLivePolicyStamp(item) ||
      lineHasFinishGuardrails(item);
    if (hasFresh) return item;
    const prev = prevById.get(item.id);
    if (
      !prev ||
      (!lineHasLiveMarkets(prev) &&
        !lineHasLivePolicyStamp(prev) &&
        !lineHasFinishGuardrails(prev))
    ) {
      return item;
    }
    const prevCond = prev.gradedCondition || prev.condition || null;
    const nextCond = item.gradedCondition || item.condition || null;
    if (
      (prev.mtgjsonUuid || null) !== (item.mtgjsonUuid || null) ||
      prevCond !== nextCond ||
      (prev.finish || null) !== (item.finish || null)
    ) {
      return item;
    }
    return {
      ...item,
      tcgLow: prev.tcgLow ?? null,
      tcgMarket: prev.tcgMarket ?? null,
      manapoolMarket: prev.manapoolMarket ?? null,
      ckBuylist: prev.ckBuylist ?? null,
      policyPrice: prev.policyPrice ?? null,
      policyPriceDebug: prev.policyPriceDebug ?? null,
      availableFinishes: prev.availableFinishes ?? null,
      finishResolveSource: prev.finishResolveSource ?? null,
      finishBlocked: prev.finishBlocked ?? false,
      finishNeedsManualPick: prev.finishNeedsManualPick ?? false,
      finishBlockMessage: prev.finishBlockMessage ?? null,
    };
  });
}

/**
 * Filter then sort lines for the receive checklist review toolbar.
 * Stable copy — does not mutate `lines`.
 */
export function filterAndSortBuylistReviewLines<T>(
  lines: readonly T[],
  opts: {
    flagFilter: BuylistReviewFlagFilter;
    sort: BuylistReviewLineSort;
    getFlags: (line: T) => readonly BuylistReviewFlag[];
    getName: (line: T) => string | null | undefined;
    getOfferUnit: (line: T) => number | null | undefined;
    getManapoolMarket: (line: T) => number | null | undefined;
  },
): T[] {
  const decorated = lines.map((line) => ({
    line,
    sortable: {
      cardName: opts.getName(line),
      offerUnit: opts.getOfferUnit(line) ?? null,
      manapoolMarket: opts.getManapoolMarket(line) ?? null,
      flags: opts.getFlags(line),
    } satisfies BuylistReviewSortableLine,
  }));

  return decorated
    .filter((row) => lineMatchesReviewFlagFilter(row.sortable.flags, opts.flagFilter))
    .sort((a, b) => compareBuylistReviewLines(a.sortable, b.sortable, opts.sort))
    .map((row) => row.line);
}
